// Front/src/pages/MyTasks.js

import React, { useEffect, useState, useCallback } from "react";
import { receive_all_relevant_tasks_with_info } from "../priorityApi";
import { get_client_from_webapi } from "../api";
import { useAuthContext } from "../hooks/useAuthContext"; // Import Auth Context
import { useNavigate } from "react-router-dom"; // To redirect to login
import { toast } from "react-toastify"; // Toast notification
import { ToastContainer } from 'react-toastify'; // For pop-up notifications
import { useLanguageContext } from '../context/LanguageContext';
import { useTranslate } from '../hooks/useTranslate';
import '../style/MyTasks.css';

const MyTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState({}); // for epanding and collapsing a service call task
  const [expandedSubtasks, setExpandedSubtasks] = useState({}); // for each service call we have subtasks.
  const [clientCheckResults, setClientCheckResults] = useState({}); // keep track if client doesn't exist a new one must be created.

  const { user } = useAuthContext(); // Get user from AuthContext
  const userId = user?.id || null; // Fallback to null if no user

  const navigate = useNavigate();

  // Post error to avoid code duplications
  const PostError = (message) => {
      toast.error(message, {
      icon: false, // Removes the default tick icon
      autoClose: 3000, // 3 seconds (default)
  });
      console.error(message);
  };    

  const toggleTask = (docno) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [docno]: !prev[docno],
    }));
  };  

  const toggleSubtask = (subId) => {
    setExpandedSubtasks((prev) => ({
      ...prev,
      [subId]: !prev[subId],
    }));
  };
  
  const t = useTranslate(); // 💬 Get translations
  const { language } = useLanguageContext(); // 🌐 Get current language

  const fetchPriorityData = useCallback(async () => {
    // timer for debugging
    //console.time("FetchPriorityData");
    try {
      const result = await receive_all_relevant_tasks_with_info(userId);
      
      // for debugging
      //console.log("result = ", result);

      if (result == null) {
        setError(t.priorityConnectionFailed);
        PostError(t.priorityConnectionFailed);
        return;
      }
      
      if (result?.error) {
        setError(t.priorityConnectionFailed);
        PostError(t.priorityConnectionFailed);
        return;
      }
      
      if (result?.data == null) {
        setError(t.priorityNoTasks);
        PostError(t.priorityNoTasks);
        return;
      }

      const tasksWithSubTasks = result?.data || [];
  
      setTasks(tasksWithSubTasks);
    } catch (err) {
      console.error(t.priorityConnectionFailed, err);
      PostError(t.priorityConnectionFailed);
      setError(t.priorityConnectionFailed);
    } finally {
      // stop timer for debugging
      // console.timeEnd("FetchPriorityData");
      setLoading(false);
    }
  }, [userId, t]); // userId is a dependency
  
  useEffect(() => {
    if (!userId) {
      PostError("Please log in.");
      navigate("/login");
      return;
    }
  
    fetchPriorityData();
  }, [navigate, userId, fetchPriorityData]);

  if (loading) return (
    <div dir={language === 'he' ? 'rtl' : 'ltr'} className="loading-container">
      <div className="spinner" />
      <div className="loading-text">{t.loadingTasks}</div>
    </div>
  );  

  const handleTestClient = async (custId, docno) => {
    try {
      const response = await get_client_from_webapi(userId, custId);
      if (response?.status === 200) {
        toast.success(t.clientExists, {     
          icon: false, // Removes the default tick icon
          autoClose: 3000, // 3 seconds (default)
        });
        setClientCheckResults(prev => ({ ...prev, [docno]: true }));
      }
      else {
        console.error("response?.error?.response?.data = ", response?.error?.response?.data);
        toast.error(`${response?.error?.response?.data || t.unknownError}`, {
          icon: false, // Removes the default tick icon
          autoClose: 3000, // 3 seconds (default)
        });
        
        setClientCheckResults(prev => ({ ...prev, [docno]: false }));
      }
    } catch (error) {
      console.error(t.clientApiCallFailed, error);
      toast.error(t.clientApiCallFailed + error, {
        icon: false, // Removes the default tick icon
        autoClose: 3000, // 3 seconds (default)
      });
    }
  };
  
  if (loading) {
    return (
      <div dir={language === 'he' ? 'rtl' : 'ltr'} className="loading-container">
        <div className="spinner" />
        <div className="loading-text">{t.loadingTasks}</div>
      </div>
    );
  }

  return (
    <div className="my-tasks-container" dir={language === 'he' ? 'rtl' : 'ltr'}>
      <h2>{t.myTasks}</h2>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      <button
        onClick={() => {
          setLoading(true);
          setError(null);
          fetchPriorityData();
        }}
        className="button-refresh-tasks"
      >
        🔄 {t.refreshTasks || "Refresh Tasks"}
      </button>

      {!error && (
        <ul className="task-list">
          {tasks.map((task, index) => (
            <li
              key={task.DOCNO}
              className={`task-item ${!task.SubTasks || task.SubTasks.length <= 0 ? 'task-no-subtasks' : ''}`}
            >
              <button
                type="button"
                onClick={() => toggleTask(task.DOCNO)}
                className={`task-card task-card-header 
                  ${expandedTasks[task.DOCNO] ? "expanded" : ""} 
                  ${(!task.SubTasks || task.SubTasks.length === 0) ? "task-card-no-subtasks" : ""}
                  ${task.SubTasks?.length > 0 && task.SubTasks.every(sub => sub.APIFLAG === null) ? "task-card-all-subtasks-completed" : ""}
                `}
              >
                <span className="task-arrow">
                  {expandedTasks[task.DOCNO] ? "🔽" : t.arrow}
                </span>
                <strong>{t.serviceNumber}</strong>&nbsp;{task.DOCNO}
              </button>

              {expandedTasks[task.DOCNO] && (
                <div className="task-expanded-section" dir={language === 'he' ? 'rtl' : 'ltr'}>
                  <div className="task-client-row" dir={language === 'he' ? 'rtl' : 'ltr'}>
                    <strong>{t.clientNumber}</strong>
                    <span>{task.CUSTNAME}</span>
                    <button
                      onClick={() => handleTestClient(task.CUSTNAME, task.DOCNO)}
                      className="button-check-client"
                    >
                      {t.checkClient}
                    </button>

                    {clientCheckResults[task.DOCNO] === false && (
                      <button
                        onClick={() => navigate("/newclient", {
                          state: {
                            custId: task.CUSTNAME,
                            CDES: task.CDES,
                            ZMOZ_ADDRESS: task.ZMOZ_ADDRESS,
                            ZMOZ_EMAIL: task.ZMOZ_EMAIL,
                            PHONENUM: task.PHONENUM,
                            NAME: task.NAME,
                            DOCNO: task.DOCNO
                          }
                        })}
                        className="button-create-client"
                      >
                        {t.createClient}
                      </button>
                    )}
                  </div>

                  <ToastContainer
                    position="top-center"
                    autoClose={3000}
                    toastClassName="custom-toast"
                    closeButton={true}
                  />

                  <strong>{t.clientName}</strong> {task.CDES}<br />
                  <strong>{t.site}</strong> {task.CODEDES}<br />
                  <strong>{t.start}</strong> {new Date(task.PDATE).toLocaleDateString('en-GB')}<br />
                  <strong>{t.contact}</strong> {task.NAME} ({task.PHONENUM})<br />
                  <strong>{t.status}</strong> {task.CALLSTATUSCODE}<br />
                  <strong>{t.comments}</strong> {task.DETAILS || "—"}<br />
                  <strong>{t.address}</strong> {task.ZMOZ_ADDRESS}<br />
                  <strong>{t.email}</strong> {task.ZMOZ_EMAIL}<br />
                  <strong>{t.totalSubtasks}</strong> {task.TotalSubtasks}

                  {task.SubTasks?.length > 0 ? (
                    <div dir={language === 'he' ? 'rtl' : 'ltr'}>
                      <strong className="section-heading">{t.subTasks}</strong>
                      <ul className="subtasks-list">
                        {task.SubTasks.map((sub, idx) => {
                          const subId = `${task.DOCNO}_${sub.SERNUM || idx}`;
                          return (
                              <li
                                key={sub.SERN}
                                className={`subtask-item 
                                  ${sub.APIFLAG === null ? 'subtask-api-flag-true' : ''}
                                  ${sub.APIFLAG !== null && sub.IsInDocnet ? 'subtask-purple' : ''}
                                `}
                                onClick={() => toggleSubtask(subId)}
                              >
                              <div className={`task-card subtask-header ${expandedSubtasks[subId] ? "expanded" : ""}`}>
                                <span className="subtask-arrow">
                                  {expandedSubtasks[subId] ? "🔽" : t.arrow}
                                </span>
                                <strong>{sub.PARTDES || "—"}</strong>&nbsp;—&nbsp;{sub.SERNUM}
                              </div>

                              {expandedSubtasks[subId] && (
                                <div className="subtask-details">
                                  <div><strong> {t.makat} </strong> {sub.Makat} </div>
                                  <div><strong> {t.serial} </strong> {sub.SERNUM} </div>
                                  <div><strong> {t.itemCode} </strong> {sub.PARTNAME} </div>
                                  <div><strong> {t.itemDesc} </strong> {sub.PARTDES} </div>
                                  <div><strong> {t.comments} </strong> {sub.REMARKS || "—"} </div>
                                  <div><strong> {t.internalId} </strong> {sub.SERN} </div>
                                  <div><strong> {t.lineNumber} </strong> {sub.Kline} </div>
                                  <div><strong> {t.existsInPriority} </strong> {sub.APIFLAG === null ? t.yes : t.no} </div>
                                  <div><strong> {t.existsInDocnet} </strong> {sub.IsInDocnet ? t.yes : t.no} </div>
                                  <div><strong> {t.symboled} </strong> {sub.Makat?.toString().endsWith("-1") ? t.yes : t.no} </div>

                                  <div className="button-subtask-wrapper">
                                    <button
                                      className="button-subtask-report"
                                      onClick={() => {

                                        if (!task || !sub) return;

                                        navigate("/newreport", {
                                          state: {
                                            PDATE: task.PDATE,
                                            DOCNO: task.DOCNO,
                                            MAKAT: sub.Makat,
                                            itemCode: sub.PARTNAME,
                                            KLINE: sub.Kline,
                                            SERNUM: sub.SERNUM,
                                            SERN: sub.SERN,
                                            custId: task.CUSTNAME,
                                            address: task.ZMOZ_ADDRESS,
                                            SAMLIL: sub.Makat?.toString().endsWith("-1") ? "Yes" : "No"
                                          }
                                        });
                                      }}
                                    >
                                      {t.newReport}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : (
                    <div>{t.noSubTasks}</div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MyTasks;